import { Analytics } from '@cobliteam/analytics';
import type { RouteDef } from '@cobliteam/dashboard/src/dashboardDef';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { useLanguage, useTranslation } from '@cobliteam/i18n';
import Input from '@cobliteam/ui/form/Input';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { getTextForLanguage } from '@cobliteam/ui-business/utils';
import { type KeyboardEvent, useRef, useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './Collapsible';
import LinkWrapper from './LinkWrapper';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu as SidebarMenuBase,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from './Sidebar';

const mobileMenuItemClassName =
  'h-11 rounded-lg px-2.5 font-medium text-gray-3 text-sm leading-5 transition-colors duration-200 hover:bg-sidebar-mobile-accent/60 hover:text-white hover:[&>svg]:text-white data-[active=true]:bg-primary data-[active=true]:font-semibold data-[active=true]:text-white data-[active=true]:[&>svg]:text-white [&>svg]:text-gray-4 motion-reduce:transition-none';

const mobileSectionTriggerClassName =
  'group/section-trigger mx-3 flex h-11 w-[calc(100%-1.5rem)] items-center justify-between gap-2 rounded-lg px-2.5 font-medium text-gray-6 text-sidebar-section leading-5 outline-none transition-colors duration-200 hover:bg-sidebar-mobile-accent/60 hover:text-white focus-visible:ring-2 focus-visible:ring-sidebar-mobile-ring focus-visible:ring-offset-0 motion-reduce:transition-none';

const mobileLinkClassName = 'rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-sidebar-mobile-ring focus-visible:ring-offset-0';

const normalizeSearch = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const getRouteTitle = (route: RouteDef, language: string) => getTextForLanguage(route.sidebar?.title, language);

const routeMatchesSearch = (route: RouteDef, search: string, language: string) => normalizeSearch(getRouteTitle(route, language)).includes(search);

const routeHasActiveDescendant = (route: RouteDef): boolean => Boolean(route.sidebar?.active || route.routes?.some(routeHasActiveDescendant));

const getVisibleRoutes = (routes: RouteDef[] | undefined): RouteDef[] | undefined => {
  if (!routes) return undefined;

  return routes
    .filter((route) => !route.sidebar?.hide)
    .map((route) => ({
      ...route,
      routes: getVisibleRoutes(route.routes),
    }));
};

const filterRoutesBySearch = (routes: RouteDef[], search: string, language: string): RouteDef[] => {
  if (!search) return getVisibleRoutes(routes) ?? [];

  return routes.reduce<RouteDef[]>((filteredRoutes, route) => {
    if (route.sidebar?.hide) return filteredRoutes;

    const matchesRoute = routeMatchesSearch(route, search, language);
    const filteredChildren = route.routes ? filterRoutesBySearch(route.routes, search, language) : undefined;

    if (matchesRoute || (filteredChildren && filteredChildren.length > 0)) {
      filteredRoutes.push({
        ...route,
        routes: matchesRoute ? getVisibleRoutes(route.routes) : filteredChildren,
      });
    }

    return filteredRoutes;
  }, []);
};

type HandleToggle = (id: string, title: string, isOpen: boolean) => void;

interface MobileRouteItemProps {
  route: RouteDef;
  currentLanguage: string;
  normalizedSearch: string;
  openGroups: Record<string, boolean>;
  onGroupToggle: HandleToggle;
  onNavigate: (destination: string) => void;
}

const MobileRouteItem = ({ route, currentLanguage, normalizedSearch, openGroups, onGroupToggle, onNavigate }: MobileRouteItemProps) => {
  const routeTitle = getRouteTitle(route, currentLanguage);
  const visibleChildren = route.routes?.filter((item) => !item.sidebar?.hide);

  if (visibleChildren?.length) {
    return (
      <SidebarGroup>
        <Collapsible
          className='group/collapsible'
          open={normalizedSearch ? true : (openGroups[route.id] ?? routeHasActiveDescendant(route))}
          onOpenChange={(isOpen) => onGroupToggle(route.id, routeTitle || '', isOpen)}
        >
          <CollapsibleTrigger className='group/trigger mx-3 flex h-11 w-[calc(100%-1.5rem)] items-center justify-between gap-2 rounded-lg px-2.5 font-medium text-gray-3 text-sm leading-5 outline-none transition-colors duration-200 hover:bg-sidebar-mobile-accent/60 hover:text-white focus-visible:ring-2 focus-visible:ring-sidebar-mobile-ring focus-visible:ring-offset-0 motion-reduce:transition-none'>
            <span className='flex min-w-0 items-center gap-3'>
              {route.sidebar?.icon && <Icon name={route.sidebar.icon as IconNames} size='md' className='shrink-0 text-gray-4 group-hover/trigger:text-white' />}
              <span className='truncate'>{routeTitle}</span>
            </span>
            <Icon
              name='chevron-down'
              size='sm'
              className='ml-auto shrink-0 text-gray-6 transition-transform duration-200 group-hover/trigger:text-white group-data-[state=open]/collapsible:rotate-180 motion-reduce:transition-none'
            />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarGroupContent>
              <SidebarMenuSub className='mx-5 my-0.5 gap-0 border-sidebar-mobile-border/60 py-0.5 pr-0 pl-3 motion-reduce:transition-none'>
                {visibleChildren.map((item) => {
                  const itemTitle = getRouteTitle(item, currentLanguage);
                  return (
                    <SidebarMenuSubItem key={item.id}>
                      <SidebarMenuSubButton
                        asChild
                        isActive={item.sidebar?.active}
                        className='min-h-11 rounded-lg px-2.5 font-medium text-gray-4 text-sm leading-5 transition-colors duration-200 hover:bg-sidebar-mobile-accent/60 hover:text-white data-[active=true]:bg-primary data-[active=true]:font-semibold data-[active=true]:text-white motion-reduce:transition-none'
                      >
                        <LinkWrapper to={item.redirect || item.url || '#'} pageId={item.id} className={mobileLinkClassName} onClick={() => onNavigate(itemTitle)}>
                          {itemTitle}
                        </LinkWrapper>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  );
                })}
              </SidebarMenuSub>
            </SidebarGroupContent>
          </CollapsibleContent>
        </Collapsible>
      </SidebarGroup>
    );
  }

  return (
    <SidebarGroup>
      <SidebarMenuBase className='px-3'>
        <SidebarMenuItem>
          <LinkWrapper to={route.redirect || route.url || '#'} pageId={route.id} className={mobileLinkClassName} onClick={() => onNavigate(routeTitle)}>
            <SidebarMenuButton tooltip={routeTitle} size='sm' className={mobileMenuItemClassName} isActive={route.sidebar?.active}>
              {route.sidebar?.icon && <Icon name={route.sidebar.icon as IconNames} size='md' />}
              <span>{routeTitle}</span>
            </SidebarMenuButton>
          </LinkWrapper>
        </SidebarMenuItem>
      </SidebarMenuBase>
    </SidebarGroup>
  );
};

interface MobileSidebarSectionProps {
  route: RouteDef;
  index: number;
  currentLanguage: string;
  normalizedSearch: string;
  isOpen: boolean;
  openGroups: Record<string, boolean>;
  onSectionToggle: HandleToggle;
  onGroupToggle: HandleToggle;
  onNavigate: (destination: string) => void;
}

const MobileSidebarSection = ({ route, index, currentLanguage, normalizedSearch, isOpen, openGroups, onSectionToggle, onGroupToggle, onNavigate }: MobileSidebarSectionProps) => {
  const routeTitle = getRouteTitle(route, currentLanguage);
  const visibleChildren = route.routes?.filter((item) => !item.sidebar?.hide);

  if (!visibleChildren?.length) {
    return (
      <SidebarGroup className={cn(index === 0 && 'mt-1')}>
        <SidebarMenuBase className='px-3'>
          <SidebarMenuItem>
            <LinkWrapper to={route.redirect || route.url || '#'} pageId={route.id} className={mobileLinkClassName} onClick={() => onNavigate(routeTitle)}>
              <SidebarMenuButton tooltip={routeTitle} size='sm' className={mobileMenuItemClassName} isActive={route.sidebar?.active}>
                {route.sidebar?.icon && <Icon name={route.sidebar.icon as IconNames} size='md' />}
                <span>{routeTitle}</span>
              </SidebarMenuButton>
            </LinkWrapper>
          </SidebarMenuItem>
        </SidebarMenuBase>
      </SidebarGroup>
    );
  }

  return (
    <SidebarGroup className={cn(index === 0 && 'mt-1')}>
      <Collapsible className='group/section' open={isOpen} onOpenChange={(nextIsOpen) => onSectionToggle(route.id, routeTitle || '', nextIsOpen)}>
        <CollapsibleTrigger data-testid='sidebar-section-title' className={mobileSectionTriggerClassName}>
          <span className='truncate'>{routeTitle}</span>
          <Icon
            name='chevron-down'
            size='sm'
            className='ml-auto shrink-0 text-gray-7 transition-transform duration-200 group-hover/section-trigger:text-white group-data-[state=open]/section:rotate-180 motion-reduce:transition-none'
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className='pt-0.5'>
            {visibleChildren.map((item) => (
              <MobileRouteItem
                key={item.id}
                route={item}
                currentLanguage={currentLanguage}
                normalizedSearch={normalizedSearch}
                openGroups={openGroups}
                onGroupToggle={onGroupToggle}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </SidebarGroup>
  );
};

export function SidebarMenuMobile() {
  const { sidebar } = useDashboard();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation('ui-business');
  const { setOpenMobile } = useSidebar();
  const [openSectionId, setOpenSectionId] = useState<string | null>();
  const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>({});
  const [search, setSearch] = useState('');
  const menuListRef = useRef<HTMLDivElement>(null);

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Tab' || event.shiftKey) return;
    const firstFocusable = menuListRef.current?.querySelector<HTMLElement>('a[href], button:not([disabled])');
    if (!firstFocusable) return;
    event.preventDefault();
    firstFocusable.focus();
  };

  const router = sidebar.router;
  const normalizedSearch = normalizeSearch(search);
  const filteredRouter = filterRoutesBySearch(router, normalizedSearch, currentLanguage);

  const handleGroupToggle = (id: string, title: string, isOpen: boolean) => {
    Analytics.track('Sidebar - Group - Toggle', { title, isOpen });
    setOpenGroups((prev) => ({
      ...prev,
      [id]: isOpen,
    }));
  };

  const handleSectionToggle = (id: string, title: string, isOpen: boolean) => {
    Analytics.track('Sidebar - Group - Toggle', { title, isOpen });
    setOpenSectionId(isOpen ? id : null);
  };

  const handleNavigate = (destination: string) => {
    setOpenMobile(false);
    Analytics.track('Sidebar - Navigate', { destination });
  };

  return (
    <div className='flex min-h-0 flex-col pb-6'>
      <div className='sticky top-0 z-10 border-sidebar-mobile-border border-b bg-sidebar-mobile px-3 py-2.5'>
        <Input
          data-testid='sidebar-mobile-search'
          name='sidebar-mobile-search'
          placeholder={t('app_sidebar.search_placeholder')}
          value={search}
          startIcon={<Icon name='search' size='md' className='text-gray-7' />}
          endIcon={
            search ? (
              <button
                type='button'
                aria-label={t('app_sidebar.clear_search')}
                className='flex size-8 items-center justify-center rounded-lg text-gray-7 transition-colors duration-200 hover:bg-gray-10 hover:text-gray-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-mobile-ring motion-reduce:transition-none'
                onClick={() => setSearch('')}
              >
                <Icon name='x' size='sm' />
              </button>
            ) : null
          }
          className='!border-sidebar-mobile-border focus-visible:!border-sidebar-mobile-ring h-10 rounded-lg bg-gray-11 text-gray-2 text-sm placeholder:text-gray-7'
          size='sm'
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
      </div>
      <div ref={menuListRef}>
        {filteredRouter.length === 0 && (
          <div className='mx-3 mt-8 flex flex-col items-center gap-2 rounded-xl border border-sidebar-mobile-border bg-gray-11/60 px-4 py-6 text-center'>
            <Icon name='search' size='md' className='text-gray-7' />
            <p className='font-medium text-gray-3 text-sm'>{t('app_sidebar.empty_search_title')}</p>
            <p className='text-gray-6 text-sm'>{t('app_sidebar.empty_search_description')}</p>
          </div>
        )}
        {filteredRouter.map((route, index) => (
          <MobileSidebarSection
            key={route.id}
            route={route}
            index={index}
            currentLanguage={currentLanguage}
            normalizedSearch={normalizedSearch}
            isOpen={normalizedSearch ? true : openSectionId === undefined ? routeHasActiveDescendant(route) : openSectionId === route.id}
            openGroups={openGroups}
            onSectionToggle={handleSectionToggle}
            onGroupToggle={handleGroupToggle}
            onNavigate={handleNavigate}
          />
        ))}
      </div>
    </div>
  );
}
